<template>
  <div class="board-email pb-100">
    <TopBarBack />
    <div class="board-email-header mx-auto">
      <p class="header-title">
        {{ $t('email.title') }}
      </p>
      <p class="header-subtitle">
        {{ $t('email.subtitle') }}
      </p>
    </div>
    <el-form
      ref="boardEmailFormRef"
      class="email-register-form mx-auto mt-default"
      :model="emailData"
      :rules="emailFormRules"
      @submit.prevent
    >
      <el-form-item
        class="email-register-item"
        prop="userName"
      >
        <el-input
          v-model="emailData.userName"
          class="email-register-input flex"
          :placeholder="$t('email.userName')"
          clearable
        >
          <template #prefix>
            <p
              v-if="!emailData.userName"
              class="asterisk"
            >
              {{ $t('entities.asterisk') }}
            </p>
          </template>
        </el-input>
      </el-form-item>
      <el-form-item
        class="email-register-item"
        prop="email"
      >
        <el-input
          v-model="emailData.email"
          class="email-register-input flex"
          :placeholder="$t('email.email')"
          clearable
        >
          <template #prefix>
            <p
              v-if="!emailData.email"
              class="asterisk"
            >
              {{ $t('entities.asterisk') }}
            </p>
          </template>
        </el-input>
      </el-form-item>
      <el-form-item
        class="email-register-item"
        prop="mobile"
      >
        <el-input
          v-model="emailData.mobile"
          type="tel"
          maxlength="11"
          class="email-register-input flex"
          :placeholder="$t('email.mobile')"
          clearable
        >
          <template #prefix>
            <p
              v-if="!emailData.mobile"
              class="asterisk"
            >
              {{ $t('entities.asterisk') }}
            </p>
          </template>
        </el-input>
      </el-form-item>
      <el-form-item
        class="email-register-item"
        prop="orderId"
      >
        <el-input
          v-model="emailData.orderId"
          class="email-register-input flex"
          :placeholder="$t('email.orderId')"
          clearable
        />
      </el-form-item>
      <el-form-item
        class="email-register-item"
        prop="title"
      >
        <el-input
          v-model="emailData.title"
          class="email-register-input flex"
          :placeholder="$t('email.titleInput')"
          clearable
        >
          <template #prefix>
            <p
              v-if="!emailData.title"
              class="asterisk"
            >
              {{ $t('entities.asterisk') }}
            </p>
          </template>
        </el-input>
      </el-form-item>
      <el-form-item
        class="email-register-item"
        prop="content"
      >
        <el-input
          v-model="emailData.content"
          class="email-register-textarea flex"
          :autosize="{ minRows: 8, maxRows: 10 }"
          type="textarea"
          show-word-limit
          maxlength="500"
          :placeholder="$t('email.content')"
        />
      </el-form-item>
      <el-form-item class="email-register-item">
        <ABottomSingleButton
          :text="$t('email.send')"
          custom-class="bottom-fixed"
          :disabled="[emailSendTrigger, emailCompleteTrigger]"
          @click-button="emailRegister(boardEmailFormRef)"
        />
      </el-form-item>
    </el-form>
    <SingleButtonDialog
      :dialog-visible-trigger="emailSendDialogTrigger"
      :dialog-title="$t('dialog.emailTitle')"
      :dialog-description="$t('dialog.emailDescription')"
      :single-text="$t('texts.confirm')"
      @click-single-button="confirmSend"
      @close-dialog="confirmSend"
    />
  </div>
</template>

<script setup lang="ts">
import type { FormInstance, FormRules } from 'element-plus'

const boardEmailFormRef = ref<FormInstance>()

const { t } = useLocale()

const { sendInquiryMail } = useApi()
const { checkPhoneNumber, checkEmail } = useUi()
const { message } = useAlarm()
const { navigate } = useCommon()

const emailData = ref<EmailInquiryForm>({
  email: '',
  title: '',
  content: '',
  userName: '',
  mobile: '',
  orderId: ''
})

const emailCompleteTrigger = ref(false)
const emailSendTrigger = ref(true)
const emailSendDialogTrigger = ref(false)

const validateName = (_rule:any, value:any, callback:any) => {
  if (!value) {
    validateFailure(callback, t('validate.name'))
  } else if (value.search(/\s/) !== -1) {
    validateFailure(callback, t('validate.nameEmpty'))
  } else {
    changeStatus(true)
    callback()
  }
}

const validateMobile = (_rule:any, value:any, callback:any) => {
  if (!value) {
    validateFailure(callback, t('validate.mobile'))
  } else if (!checkPhoneNumber(value)) {
    validateFailure(callback, t('validate.mobileForm'))
  } else if (value.search(/\s/) !== -1 || value.search(/[a-zA-Z]/) !== -1) {
    validateFailure(callback, t('validate.mobileEmpty'))
  } else {
    changeStatus(true)
    callback()
  }
}

const validateEmail = (_rule:any, value:any, callback:any) => {
  if (!value) {
    validateFailure(callback, t('validate.email'))
  } else if (!checkEmail(value)) {
    validateFailure(callback, t('validate.emailForm'))
  } else if (value.search(/\s/) !== -1) {
    validateFailure(callback, t('validate.emailEmpty'))
  } else {
    changeStatus(true)
    callback()
  }
}

const emailFormRules = reactive<FormRules>({
  userName: [{ required: true, validator: validateName, trigger: 'blur' }],
  email: [{ required: true, validator: validateEmail, trigger: 'blur' }],
  mobile: [{ required: true, validator: validateMobile, trigger: 'blur' }],
  title: [{ required: true, message: t('validate.title'), trigger: 'blur' }],
  content: [{ required: true, message: t('validate.message'), trigger: 'blur' }]
})

const props = withDefaults(
  defineProps<{
    partnersKey?: string
  }>(),
  {
    partnersKey: ''
  }
)

watchEffect(() => {
  emailData.value.email && emailData.value.title && emailData.value.userName && emailData.value.mobile && emailData.value.content
    ? emailCompleteTrigger.value = true
    : emailCompleteTrigger.value = false
})

const emailRegister = async (formEl:FormInstance | undefined) => {
  if (!formEl) { return }
  await formEl.validate((valid) => {
    if (valid) {
      const sendData = {
        email: emailData.value.email,
        title: emailData.value.title,
        content: t('texts.content').concat(t('entities.colon'), ' ', emailData.value.content),
        userName: t('texts.userName').concat(t('entities.colon'), ' ', emailData.value.userName),
        mobile: t('texts.mobile').concat(t('entities.colon'), ' ', emailData.value.mobile),
        orderId: t('texts.orderNumber').concat(t('entities.colon'), ' ', emailData.value?.orderId ?? '')
      }
      sendInquiryMail(sendData).then((res:any) => {
        res?.success
          ? emailSendDialogTrigger.value = true
          : message('info', res.error.message, false, true, 2000, 90, 'message-plain')
      })
    } else {
      emailSendTrigger.value = false
      message('info', t('validate.formCheck'), false, true, 2000, 90, 'message-plain')
      formEl.resetFields()
    }
  })
}

const confirmSend = () => {
  emailSendDialogTrigger.value = false
  navigate('/', props.partnersKey)
}

const changeStatus = (trigger:boolean) => {
  emailSendTrigger.value = trigger
}

const validateFailure = (callback:any, message:string) => {
  changeStatus(false)
  return callback(new Error(message))
}

</script>
